/**
 * ConflictWarningsPanel (FLOW-577)
 *
 * Read-only measurement surface for FLOW-573's contradiction detector: the
 * counts and acceptance rate the roadmap's Phase 8 exit criterion is judged
 * by, plus the most recent warnings so a human can tell signal from noise.
 * Resolving a warning happens in the capture flow, where the context for a
 * given decision actually is — this panel only reads.
 *
 * Collapsed by default so it doesn't compete with the decision search this
 * page exists for, and fetches nothing until first expanded.
 */

import {
  Accessor,
  Component,
  For,
  Show,
  createResource,
  createSignal,
} from "solid-js";
import { Spinner } from "../ui";
import {
  getConflictStats,
  listConflictWarnings,
} from "../../api/decisionConflicts";
import type { ProjectId } from "../../api/types";

const OUTCOME_LABELS: Record<string, string> = {
  pending: "Pending",
  accepted_superseded: "Accepted – superseded",
  accepted_edited: "Accepted – edited",
  accepted_linked: "Accepted – linked",
  dismissed_not_a_conflict: "Dismissed – not a conflict",
  dismissed_intentional: "Dismissed – intentional",
};

function outcomeLabel(outcome: string): string {
  return OUTCOME_LABELS[outcome] ?? outcome;
}

function capitalize(value: string): string {
  return value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value;
}

function formatRate(rate: number | null): string {
  return rate === null ? "Not yet measured" : `${Math.round(rate * 100)}%`;
}

const StatTile: Component<{ label: string; value: number }> = (props) => (
  <div class="bg-sunken border border-line rounded px-3 py-2">
    <div class="text-xs text-ink-3">{props.label}</div>
    <div class="text-lg font-semibold text-ink">{props.value}</div>
  </div>
);

export interface ConflictWarningsPanelProps {
  projectId: Accessor<ProjectId>;
}

export const ConflictWarningsPanel: Component<ConflictWarningsPanelProps> = (
  props,
) => {
  const [expanded, setExpanded] = createSignal(false);

  const [stats, { refetch: refetchStats }] = createResource(
    () => (expanded() ? props.projectId() : undefined),
    (projectId) => getConflictStats(projectId),
  );
  const [warningsResp, { refetch: refetchWarnings }] = createResource(
    () => (expanded() ? props.projectId() : undefined),
    (projectId) => listConflictWarnings(projectId, { limit: 20 }),
  );

  // Guard on `.state`, not the accessor directly: reading an errored
  // resource throws the stored error, which would abort this computation
  // mid-flight and leave the error banner below unrendered.
  const warnings = () =>
    warningsResp.state === "errored" ? [] : (warningsResp()?.warnings ?? []);

  const loading = () => stats.loading || warningsResp.loading;
  const hasError = () => Boolean(stats.error) || Boolean(warningsResp.error);
  const hasData = () => !loading() && !hasError() && stats.state === "ready";

  const retry = () => {
    refetchStats();
    refetchWarnings();
  };

  return (
    <div class="mx-4 mt-4 bg-surface rounded border border-line">
      <button
        type="button"
        class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-sunken transition-colors rounded"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded()}
      >
        <svg
          class={
            "w-4 h-4 text-ink-3 flex-shrink-0 transition-transform " +
            (expanded() ? "rotate-90" : "")
          }
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
        <span class="text-sm font-medium text-ink">Conflict warnings</span>
      </button>

      <Show when={expanded()}>
        <div class="px-4 pb-4 border-t border-line">
          <Show when={loading()}>
            <div role="status" class="flex justify-center py-8">
              <Spinner />
              <span class="sr-only">Loading conflict warnings…</span>
            </div>
          </Show>

          <Show when={!loading() && hasError()}>
            <div class="mt-3 bg-signal-red-tint border border-signal-red/25 rounded p-3 text-signal-red text-sm">
              Failed to load conflict warnings.{" "}
              <button
                type="button"
                class="underline hover:no-underline"
                onClick={retry}
              >
                Try again
              </button>
            </div>
          </Show>

          <Show when={hasData() && stats()}>
            {(readyStats) => (
              <Show
                when={readyStats().shown > 0}
                fallback={
                  <p class="mt-3 text-sm text-ink-3">
                    No conflict warnings have been raised for this project yet.
                  </p>
                }
              >
                <div
                  role="group"
                  aria-label="Conflict warning totals"
                  class="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2"
                >
                  <StatTile label="Shown" value={readyStats().shown} />
                  <StatTile label="Pending" value={readyStats().pending} />
                  <StatTile label="Accepted" value={readyStats().accepted} />
                  <StatTile label="Dismissed" value={readyStats().dismissed} />
                </div>

                <div class="mt-3 flex items-baseline gap-2">
                  <span class="text-xs text-ink-3">Acceptance rate</span>
                  <span class="text-sm font-medium text-ink">
                    {formatRate(readyStats().acceptance_rate)}
                  </span>
                </div>

                <Show when={readyStats().by_confidence.length > 0}>
                  <div class="mt-4">
                    <h4 class="text-xs font-medium text-ink-3 mb-1.5">
                      By confidence
                    </h4>
                    <ul class="divide-y divide-line border border-line rounded overflow-hidden">
                      <For each={readyStats().by_confidence}>
                        {(bucket) => (
                          <li class="flex items-center justify-between px-3 py-1.5 text-sm">
                            <span class="text-ink-2">
                              {capitalize(bucket.key)}
                            </span>
                            <span class="flex items-center gap-2 text-ink-3">
                              <span>
                                {bucket.accepted} accepted, {bucket.dismissed}{" "}
                                dismissed of {bucket.shown}
                              </span>
                              <span class="font-medium text-ink-2">
                                {formatRate(bucket.acceptance_rate)}
                              </span>
                            </span>
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </Show>

                <div class="mt-4">
                  <h4 class="text-xs font-medium text-ink-3 mb-1.5">
                    Recent warnings
                  </h4>
                  <Show
                    when={warnings().length > 0}
                    fallback={
                      <p class="text-sm text-ink-3">
                        No recent warnings to show.
                      </p>
                    }
                  >
                    <ul class="divide-y divide-line border border-line rounded overflow-hidden">
                      <For each={warnings()}>
                        {(warning) => (
                          <li class="px-3 py-2">
                            <div class="flex items-center gap-2 flex-wrap">
                              <span class="text-sm font-medium text-ink">
                                {warning.proposed_title}
                              </span>
                              <span class="text-xs text-ink-3">
                                conflicts with
                              </span>
                              <span class="text-sm text-ink-2">
                                {warning.existing_title}
                              </span>
                            </div>
                            <p class="mt-1 text-sm text-ink-2">
                              {warning.explanation}
                            </p>
                            <div class="mt-1 flex items-center gap-2 text-xs text-ink-3">
                              <span>Confidence: {warning.confidence}</span>
                              <span aria-hidden="true">·</span>
                              <span>{outcomeLabel(warning.outcome)}</span>
                            </div>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </div>
              </Show>
            )}
          </Show>
        </div>
      </Show>
    </div>
  );
};

export default ConflictWarningsPanel;
